import { ApiError, useProvisionPartner } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
} from '@hostesa/ui';
import type * as React from 'react';
import { useState } from 'react';

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PLATFORM_BASE = 'https://platform.hostesa.ai';

interface FormState {
  partnerName: string;
  partnerSlug: string;
  hotelName: string;
  hotelSlug: string;
  timezone: string;
  adminEmail: string;
  adminName: string;
  password: string;
}

const EMPTY: FormState = {
  partnerName: '',
  partnerSlug: '',
  hotelName: '',
  hotelSlug: '',
  timezone: 'Europe/Sofia',
  adminEmail: '',
  adminName: '',
  password: '',
};

type StepKey = 'partner' | 'hotel' | 'admin' | 'review';
const STEPS: StepKey[] = ['partner', 'hotel', 'admin', 'review'];

/**
 * Operator wizard to register a new partner (tenant + first hotel + first admin) via
 * `POST /operator/partners`. Controlled 4-step flow with per-step validation; on success a setup
 * checklist deep-links into the new hotel's platform setup.
 */
export function RegisterPage() {
  const { t } = useT('backoffice');
  const provision = useProvisionPartner();

  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const step: StepKey = STEPS[stepIndex] ?? 'partner';
  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  function validateStep(which: StepKey): Partial<Record<keyof FormState, string>> {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (which === 'partner') {
      if (!form.partnerName.trim()) errs.partnerName = t('register.errors.required');
      if (!SLUG.test(form.partnerSlug)) errs.partnerSlug = t('register.errors.slug');
    }
    if (which === 'hotel') {
      if (!form.hotelName.trim()) errs.hotelName = t('register.errors.required');
      if (!SLUG.test(form.hotelSlug)) errs.hotelSlug = t('register.errors.slug');
      if (!form.timezone.trim()) errs.timezone = t('register.errors.required');
    }
    if (which === 'admin') {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.adminEmail))
        errs.adminEmail = t('register.errors.email');
      if (form.password.length < 10) errs.password = t('register.errors.passwordShort');
    }
    return errs;
  }

  function next() {
    const errs = validateStep(step);
    setErrors(errs);
    if (Object.keys(errs).length === 0) setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  }
  function back() {
    setErrors({});
    setStepIndex((i) => Math.max(i - 1, 0));
  }

  function submit() {
    setErrors({});
    provision.mutate(
      {
        partner: { name: form.partnerName.trim(), slug: form.partnerSlug },
        hotel: {
          name: form.hotelName.trim(),
          slug: form.hotelSlug,
          timezone: form.timezone.trim(),
        },
        admin: {
          email: form.adminEmail.trim(),
          fullName: form.adminName.trim() || undefined,
          password: form.password,
        },
      },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.isConflict) {
            // Slug collision — send the operator back to the partner step.
            setErrors({ partnerSlug: t('register.errors.slugTaken') });
            setStepIndex(0);
          }
        },
      },
    );
  }

  // ── Success: setup checklist ────────────────────────────────────────────
  if (provision.isSuccess) {
    const r = provision.data;
    const setupHref = (suffix: string) =>
      `${PLATFORM_BASE}/${r.tenant.slug}/${r.hotel.slug}/${suffix}`;
    return (
      <section data-testid="register-page" className="mx-auto w-full max-w-3xl px-8 py-8">
        <PageHeader eyebrow={t('register.eyebrow')} title={t('register.title')} />
        <Card data-testid="register-success">
          <CardHeader>
            <CardTitle>{t('register.success.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-[var(--color-stone-mid)]">
              {t('register.success.description')}
            </p>
            <dl className="flex flex-col gap-1 text-sm">
              <Row
                label={t('register.success.partnerLabel')}
                value={`${r.tenant.name} · ${r.tenant.slug}`}
              />
              <Row
                label={t('register.success.hotelLabel')}
                value={`${r.hotel.name} · ${r.hotel.slug}`}
              />
              <Row label={t('register.success.adminLabel')} value={r.adminUser.email} />
            </dl>

            <div className="mt-2 flex flex-col gap-2">
              <h3 className="font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]">
                {t('register.checklist.title')}
              </h3>
              {(
                [
                  ['voice', 'settings'],
                  ['phone', 'settings'],
                  ['knowledge', 'onboarding'],
                ] as const
              ).map(([key, suffix]) => (
                <div
                  key={key}
                  data-testid={`checklist-${key}`}
                  className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] py-2 last:border-0"
                >
                  <span className="text-sm text-[var(--color-charcoal)]">
                    {t(`register.checklist.${key}`)}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]">
                      {t('register.checklist.notConfigured')}
                    </span>
                    <a
                      data-testid={`checklist-${key}-link`}
                      href={setupHref(suffix)}
                      className="text-xs font-medium text-[var(--color-charcoal)] underline"
                    >
                      {t('register.checklist.openSetup')}
                    </a>
                  </span>
                </div>
              ))}
            </div>

            <Button
              data-testid="register-another-btn"
              variant="secondary"
              size="sm"
              className="self-start"
              onClick={() => {
                provision.reset();
                setForm(EMPTY);
                setErrors({});
                setStepIndex(0);
              }}
            >
              {t('register.actions.registerAnother')}
            </Button>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="register-page" className="mx-auto w-full max-w-3xl px-8 py-8">
      <PageHeader
        eyebrow={t('register.eyebrow')}
        title={t('register.title')}
        description={t('register.description')}
      />

      {/* Step indicator */}
      <ol
        data-testid="register-steps"
        className="mb-6 flex items-center gap-2 font-[var(--font-mono)] text-[11px] uppercase tracking-widest"
      >
        {STEPS.map((s, i) => (
          <li
            key={s}
            className={
              i === stepIndex
                ? 'rounded-full bg-[var(--color-charcoal)] px-2.5 py-1 text-[var(--color-alabaster)]'
                : 'px-2.5 py-1 text-[var(--color-stone-mid)]'
            }
          >
            {t(`register.steps.${s}`)}
          </li>
        ))}
      </ol>

      <Card>
        <CardContent className="flex flex-col gap-4 py-6">
          {step === 'partner' && (
            <>
              <Field
                id="register-partner-name"
                label={t('register.fields.partnerName')}
                placeholder={t('register.fields.partnerNamePlaceholder')}
                value={form.partnerName}
                onChange={set('partnerName')}
                error={errors.partnerName}
              />
              <Field
                id="register-partner-slug"
                label={t('register.fields.partnerSlug')}
                placeholder={t('register.fields.slugPlaceholder')}
                value={form.partnerSlug}
                onChange={set('partnerSlug')}
                error={errors.partnerSlug}
              />
            </>
          )}
          {step === 'hotel' && (
            <>
              <Field
                id="register-hotel-name"
                label={t('register.fields.hotelName')}
                placeholder={t('register.fields.hotelNamePlaceholder')}
                value={form.hotelName}
                onChange={set('hotelName')}
                error={errors.hotelName}
              />
              <Field
                id="register-hotel-slug"
                label={t('register.fields.hotelSlug')}
                placeholder={t('register.fields.slugPlaceholder')}
                value={form.hotelSlug}
                onChange={set('hotelSlug')}
                error={errors.hotelSlug}
              />
              <Field
                id="register-timezone"
                label={t('register.fields.timezone')}
                placeholder={t('register.fields.timezonePlaceholder')}
                value={form.timezone}
                onChange={set('timezone')}
                error={errors.timezone}
              />
            </>
          )}
          {step === 'admin' && (
            <>
              <Field
                id="register-admin-email"
                label={t('register.fields.adminEmail')}
                placeholder={t('register.fields.adminEmailPlaceholder')}
                value={form.adminEmail}
                onChange={set('adminEmail')}
                error={errors.adminEmail}
              />
              <Field
                id="register-admin-name"
                label={t('register.fields.adminName')}
                placeholder={t('register.fields.adminNamePlaceholder')}
                value={form.adminName}
                onChange={set('adminName')}
              />
              <Field
                id="register-password"
                label={t('register.fields.password')}
                type="password"
                value={form.password}
                onChange={set('password')}
                error={errors.password}
                help={t('register.fields.passwordHelp')}
              />
            </>
          )}
          {step === 'review' && (
            <dl data-testid="register-review" className="flex flex-col gap-2 text-sm">
              <Row
                label={t('register.review.partner')}
                value={`${form.partnerName} · ${form.partnerSlug}`}
              />
              <Row
                label={t('register.review.hotel')}
                value={`${form.hotelName} · ${form.hotelSlug} · ${form.timezone}`}
              />
              <Row label={t('register.review.admin')} value={form.adminEmail} />
            </dl>
          )}

          {provision.isError && (
            <p data-testid="register-error" className="text-sm text-[var(--color-alert)]">
              {provision.error instanceof ApiError && provision.error.isConflict
                ? t('register.errors.slugTaken')
                : t('register.errors.generic')}
            </p>
          )}

          <div className="mt-2 flex items-center justify-between">
            <Button
              data-testid="register-back-btn"
              variant="ghost"
              size="sm"
              onClick={back}
              disabled={stepIndex === 0}
            >
              {t('register.actions.back')}
            </Button>
            {step === 'review' ? (
              <Button
                data-testid="register-create-btn"
                size="sm"
                onClick={submit}
                disabled={provision.isPending}
              >
                {provision.isPending
                  ? t('register.actions.creating')
                  : t('register.actions.create')}
              </Button>
            ) : (
              <Button data-testid="register-next-btn" size="sm" onClick={next}>
                {t('register.actions.next')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] py-1.5 last:border-0">
      <dt className="text-[var(--color-stone-mid)]">{label}</dt>
      <dd className="font-medium text-[var(--color-charcoal)]">{value}</dd>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type,
  error,
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  error?: string;
  help?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        data-testid={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      {help && <span className="text-xs text-[var(--color-stone-mid)]">{help}</span>}
      {error && (
        <span data-testid={`${id}-error`} className="text-xs text-[var(--color-alert)]">
          {error}
        </span>
      )}
    </div>
  );
}
